import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  backendApis,
  type SimulatorChannel,
  type SimulatorEvent,
} from '@/backendApis';
import { getEnvVar } from '@/env';

const EVENTS_POLL_INTERVAL_MS = 1000;

export const simulatorKeys = {
  all: ['channelSimulator'] as const,
  status: () => [...simulatorKeys.all, 'status'] as const,
  session: (sessionId: string) =>
    [...simulatorKeys.all, 'session', sessionId] as const,
  events: (sessionId: string) =>
    [...simulatorKeys.session(sessionId), 'events'] as const,
};

export const simulatorStatusQueryOptions = () =>
  queryOptions({
    queryKey: simulatorKeys.status(),
    queryFn: () => backendApis.channelSimulator.getStatus(),
    enabled: getEnvVar('IS_RUNNING_IN_LOCALHOST'),
    retry: false,
    staleTime: 60_000,
  });

export const useSimulatorStatusQuery = () =>
  useQuery(simulatorStatusQueryOptions());

export const useSimulatorSessionQuery = (sessionId: string) =>
  useQuery({
    queryKey: simulatorKeys.session(sessionId),
    queryFn: () => backendApis.channelSimulator.getSession(sessionId),
    retry: false,
    staleTime: 60_000,
  });

/** The events of a session the page holds, and the log generation they came from. */
export type SimulatorEventLog = {
  generation: string | null;
  events: SimulatorEvent[];
};

export const fetchNewEvents = async (
  queryClient: QueryClient,
  sessionId: string
): Promise<SimulatorEventLog> => {
  const previous = queryClient.getQueryData<SimulatorEventLog>(
    simulatorKeys.events(sessionId)
  ) ?? { generation: null, events: [] };
  const after = previous.events.at(-1)?.seq ?? 0;
  const page = await backendApis.channelSimulator.listEvents(
    sessionId,
    after,
    previous.generation
  );
  // A new generation means the log was cleared since the last read, and the page holds all of it
  if (page.generation !== previous.generation) return page;
  return page.events.length > 0
    ? { ...previous, events: [...previous.events, ...page.events] }
    : previous;
};

export const useSimulatorEventsQuery = (sessionId: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: simulatorKeys.events(sessionId),
    queryFn: () => fetchNewEvents(queryClient, sessionId),
    refetchInterval: EVENTS_POLL_INTERVAL_MS,
    retry: false,
  });
};

export const useSendSimulatorMessageMutation = (
  sessionId: string,
  channel: SimulatorChannel
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ text, attachment }: { text: string; attachment?: File }) =>
      backendApis.channelSimulator.sendMessage(
        sessionId,
        channel,
        text,
        attachment
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: simulatorKeys.events(sessionId),
      }),
  });
};

export const useSimulatorLinkCodeMutation = (channel: SimulatorChannel) =>
  useMutation({
    mutationFn: () => backendApis.channelSimulator.createLinkCode(channel),
  });

export const useClearSimulatorEventsMutation = (sessionId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    onMutate: () =>
      queryClient.cancelQueries({ queryKey: simulatorKeys.events(sessionId) }),
    mutationFn: () => backendApis.channelSimulator.clearEvents(sessionId),
    onSuccess: () => {
      queryClient.setQueryData<SimulatorEventLog>(
        simulatorKeys.events(sessionId),
        { generation: null, events: [] }
      );
    },
  });
};
